import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../styles/UpdatePerformanceTable.css';

const UpdatePerformanceTable = ({ studentId, openUpdatePerformancesDialogExternally, setOpenUpdatePerformacesDialogExternally }) => {
  const [performances, setPerformances] = useState([]);
  const [performance, setPerformance] = useState({ note: '', rating: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingPerformanceId, setEditingPerformanceId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  const API_URL = process.env.REACT_APP_API_URL;


  useEffect(() => {
    if (openUpdatePerformancesDialogExternally) {
      setShowDialog(true);
      setOpenUpdatePerformacesDialogExternally(false); // Reset parent signal
    }
  }, [openUpdatePerformancesDialogExternally, setOpenUpdatePerformacesDialogExternally]);

  const handleChange = (e) => {
    setPerformance({ ...performance, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const validatePerformance = () => {
    const errors = {};
    if (!performance.note.trim()) errors.note = 'Note is required';
    if (!performance.rating.trim()) errors.rating = 'Rating is required';
    return errors;
  };

  const fetchPerformances = useCallback(async () => {
    if (!studentId) {
      setPerformances([]);
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/student-performance/${studentId}`);
      const response = res.data.studentPerformance || [];
      setPerformances(response);
    } catch (err) {
      console.error('Error fetching performance:', err);
      setPerformances([]);
    }
  }, [studentId, API_URL]); // ✅ Include dependencies

  // ✅ Now it's safe to use it inside useEffect
  useEffect(() => {
    fetchPerformances();
  }, [fetchPerformances]); // ✅ Warning will be resolved

  const handleAddOrUpdatePerformance = async () => {
   const validationErrors = validatePerformance();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }
    try {
      const performanceData = {
        ...performance,
        studentId,
      };

      if (isEditing && editingPerformanceId) {
        await axios.put(`${API_URL}/api/student-performance/${editingPerformanceId}`, performanceData);
      } else {
        await axios.post(`${API_URL}/api/student-performance`, performanceData);
      }

      fetchPerformances();
    } catch (err) {
      console.error('Error saving performance:', err);
    }

    setPerformance({ note: '', rating: '' });
    setIsEditing(false);
    setEditingPerformanceId(null);
    setErrors({});
    setShowDialog(false);
  };

  const handleRowClick = (performanceData) => {
    setPerformance({
      note: performanceData.note,
      rating: performanceData.rating,
    });
    setIsEditing(true);
    setEditingPerformanceId(performanceData._id);
    setShowDialog(true);
  };

  const handleDeletePerformance = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeletePerformance = async () => {
    if (!editingPerformanceId) return;
    try {
      await axios.delete(`${API_URL}/api/student-performance/${editingPerformanceId}`);
      fetchPerformances();
    } catch (err) {
      console.error('Error deleting performance:', err);
    }
    handleCancel();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleCancel = () => {
    setPerformance({ note: '', rating: '' });
    setIsEditing(false);
    setEditingPerformanceId(null);
    setErrors({});
    setShowDialog(false);
  };

  return (
    <div className="note-container">
      {performances.length > 0 && (<div className="note-header">
        <h3>Student Performance</h3>
      </div>
      )}

      {showDialog && (
        <div className="dialog-backdrop">
          <div className="performance-dialog-box">
            <button className="cancel-performance-btn" onClick={handleCancel}>
              ×
            </button>
            <h4>{isEditing ? 'Edit Performance' : 'Add Performance'}</h4>

            <div className="form-group">
              <label htmlFor="note">Note</label>
              <input id="note" name="note" value={performance.note} onChange={handleChange} />
              {errors.note && <div className="error-text">{errors.note}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="rating">Rating</label>
              <input id="rating" name="rating" value={performance.rating} onChange={handleChange} />
              {errors.rating && <div className="error-text">{errors.rating}</div>}
            </div>

            <div className="performance-dialog-actions">
              {isEditing && (
                <button type="button" className="update-performance-delete-btn" onClick={handleDeletePerformance}>
                  Delete
                </button>
              )}
              <button type="button" className="update-performance-btn" onClick={handleAddOrUpdatePerformance}>
                {isEditing ? 'Update Performance' : 'Save Performance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="performance-popup-backdrop">
          <div className="performance-popup-box">
            <p>Please fill all required fields before adding a performance.</p>
            <button onClick={() => setShowPopup(false)}>OK</button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="performance-popup-backdrop">
          <div className="performance-popup-box delete-confirm">
            <p>Are you sure you want to delete this performance?</p>
            <div className="delete-performance-confirm-actions">
              <button type="button" className="btn-performance-cancel" onClick={cancelDelete}>Cancel</button>
              <button type="button" className="btn-performance-ok" onClick={confirmDeletePerformance}>OK</button>
            </div>
          </div>
        </div>
      )}

      {performances.length > 0 && (<table className="note-table">
        <thead>
          <tr>
            <th>Note</th>
            <th>Rating</th>
          </tr>
        </thead>
        <tbody>
          {performances.length === 0 ? (
            <tr>
              <td colSpan="2">No performance data added.</td>
            </tr>
          ) : (
            performances.map((item) => (
              <tr key={item._id} onClick={() => handleRowClick(item)}>
                <td>{item.note}</td>
                <td>{item.rating}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      )}
    </div>
  );
};

export default UpdatePerformanceTable;
